import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { switchOrganization } from '../services/userService';
import { useAuthActions } from '../stores/authStore';
import { useAnalysisRunsStore } from '../stores/analysisRunsStore';
import { useArtifactStore } from '../stores/artifactStore';
import { useCadrageStore } from '../stores/cadrageStore';
import { useAIFillerStore } from '../stores/aiFillerStore';
import { extractApiErrorMessage } from '../lib/apiError';
import notify from '../lib/notify';

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Admin consoles are cross-org: their detail pages stay valid after a switch.
const CROSS_ORG_PREFIXES = new Set(['organizations', 'users', 'admin', 'forges', 'applications', 'forge-dashboard']);

/** True when the path opens one object (an id segment), which the new org will not have. */
export function isObjectDetailPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0 || CROSS_ORG_PREFIXES.has(parts[0])) return false;
  return parts.some((p) => UUID_SEGMENT.test(p));
}

/**
 * Stores that cache data of the current organization outside the routed pages.
 * Routed pages remount on their own (AppLayout keys the <Outlet> on organization_id);
 * branding, UI settings and the org name reload from their organization_id effects.
 */
function resetOrganizationScopedStores(): void {
  useAnalysisRunsStore.getState().clear();
  useArtifactStore.setState({
    sessions: [],
    current: null,
    activeContext: null,
    expanded: null,
    pendingMessage: null,
    error: null,
  });
  useCadrageStore.setState({
    cadrageData: null,
    currentUseCaseDetail: null,
    documentTree: null,
    interviews: [],
    selectedInterviewId: null,
    error: null,
    loadError: null,
  });
  useAIFillerStore.getState().reset();
}

export interface SwitchOptions {
  /** Keep the current URL (the 404 "switch to <org>" button reopens the same object). */
  stayOnPage?: boolean;
}

/** Super-admin org switch: PUT /users/me/organization, then refresh everything org-bound. */
export function useSwitchOrganization() {
  const { updateUser } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [switching, setSwitching] = useState(false);

  const switchTo = useCallback(
    async (organizationId: string, options: SwitchOptions = {}): Promise<boolean> => {
      setSwitching(true);
      try {
        const user = await switchOrganization(organizationId);
        resetOrganizationScopedStores();
        updateUser(user);
        if (!options.stayOnPage && isObjectDetailPath(location.pathname)) {
          navigate('/', { replace: true });
        }
        return true;
      } catch (err) {
        notify.error(extractApiErrorMessage(err, t('orgSwitcher.error')));
        return false;
      } finally {
        setSwitching(false);
      }
    },
    [updateUser, navigate, location.pathname, t]
  );

  return { switchTo, switching };
}
